import { useEffect, useState } from "react";
import axios from "axios";
import { Film, Loader2, ArrowRight, Heart, ShieldCheck, MapPin } from "lucide-react";
import { useTranslation } from "@/i18n";
import LanguagePicker from "@/LanguagePicker";
import { mediaUrl } from "@/mediaUrl";
import HoverPreviewThumb from "@/HoverPreviewThumb";
import { usePublicConfig } from "@/usePublicConfig";
import { resolveDonationUrl, getStoryStatus } from "@/donateUrl";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TONE_ACCENT = {
  urgent: "border-l-[3px] border-l-rose-500/80",
  uplifting: "border-l-[3px] border-l-emerald-500/80",
  informational: "border-l-[3px] border-l-sky-500/60",
};

export default function ImpactPage() {
  const [t] = useTranslation();
  const [stories, setStories] = useState(null);
  const [active, setActive] = useState("all");
  const [error, setError] = useState("");

  const FILTERS = [
    { id: "all",            label: t("filter_all") },
    { id: "animal_rescue",  label: t("filter_animals") },
    { id: "social_impact",  label: t("filter_people") },
    { id: "environment",    label: t("filter_environment") },
    { id: "charity",        label: t("filter_charities") },
    { id: "urgent",         label: t("filter_urgent"),    kind: "tone" },
    { id: "uplifting",      label: t("filter_uplifting"), kind: "tone" },
  ];

  const filter = FILTERS.find((f) => f.id === active);
  useEffect(() => {
    let cancelled = false;
    const params = {};
    if (active !== "all") {
      if (filter?.kind === "tone") params.tone = active;
      else params.category = active;
    }
    setStories(null);
    axios.get(`${API}/stories/impact`, { params })
      .then((r) => { if (!cancelled) setStories(r.data); })
      .catch(() => { if (!cancelled) setError("Couldn't load impact stories. Please retry shortly."); });
    return () => { cancelled = true; };
  }, [active]);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen" data-testid="impact-page">
      <header className="px-6 md:px-10 py-6 md:py-8 border-b border-white/5">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="home-link">
            <img src="/brand-s.png" alt="Storylo" className="w-9 h-9 rounded-full" data-testid="brand-logo" />
            <div>
              <div className="font-display text-lg leading-none">Storylo</div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--muted)] mt-1">
                {t("tagline")}
              </div>
            </div>
          </a>
          <nav className="flex items-center gap-3 sm:gap-4 text-sm">
            <a href="/" className="text-[color:var(--muted)] hover:text-white transition-colors">{t("nav_all")}</a>
            <a href="/impact" className="text-white">{t("nav_impact")}</a>
            <LanguagePicker />
          </nav>
        </div>
      </header>

      <main className="px-6 md:px-10 py-12 md:py-16">
        <div className="max-w-[1200px] mx-auto">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            {t("impact_h1_a")}
            <em className="block text-[color:var(--accent)] not-italic mt-2">{t("impact_h1_b")}</em>
          </h1>
          <p className="text-[color:var(--muted)] max-w-2xl mt-5 leading-relaxed text-base sm:text-lg">
            {t("impact_sub")}
          </p>

          <div className="mt-10 flex flex-wrap gap-2" data-testid="impact-filters">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setActive(f.id)}
                className={`text-sm px-4 py-2 rounded-full border transition-colors ${
                  active === f.id
                    ? "border-[color:var(--accent)] text-[color:var(--accent)] bg-[color:var(--accent)]/5"
                    : "border-white/10 text-[color:var(--muted)] hover:text-white hover:border-white/30"
                }`}
                data-testid={`impact-filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {error && (
            <p className="mt-12 text-center text-[color:var(--muted)]">{error}</p>
          )}

          {!error && stories === null && (
            <div className="mt-16 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-[color:var(--accent)]" />
            </div>
          )}

          {!error && stories && stories.length === 0 && (
            <div className="mt-16 panel p-10 text-center">
              <Film className="w-10 h-10 mx-auto text-[color:var(--muted)] opacity-50" />
              <div className="font-display text-lg mt-4">{t("impact_empty_title")}</div>
              <div className="text-sm text-[color:var(--muted)] mt-2">
                {t("impact_empty_sub")}
              </div>
            </div>
          )}

          {!error && stories && stories.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12 md:mt-16" data-testid="impact-grid">
              {stories.map((s) => (
                <ImpactCard key={s.slug} story={s} t={t} />
              ))}
            </div>
          )}
        </div>
      </main>

      <footer className="mt-16 border-t border-white/5 px-6 md:px-10 py-6">
        <div className="max-w-[1200px] mx-auto flex flex-wrap items-center justify-between gap-3 text-xs text-[color:var(--muted)]">
          <div>
            {t("footer_made_by")} <a href="https://hadfun.co.uk" className="text-white hover:text-[color:var(--accent)] transition-colors">Hadfun</a>
          </div>
          <a href="/studio" className="opacity-30 hover:opacity-100 transition-opacity">{t("nav_studio")}</a>
        </div>
      </footer>
    </div>
  );
}

function ImpactCard({ story: s, t }) {
  const tone = TONE_ACCENT[s.emotional_tone] || TONE_ACCENT.informational;
  const publicCfg = usePublicConfig();
  const donate = resolveDonationUrl(s, publicCfg);
  const status = getStoryStatus(s);
  const CATEGORY_LABEL = {
    charity: t("cat_charity"),
    animal_rescue: t("cat_animal_rescue"),
    social_impact: t("cat_social_impact"),
    environment: t("cat_environment"),
  };
  return (
    <div className={`panel overflow-hidden group block ${tone}`} data-testid={`impact-card-${s.slug}`}>
      <a href={`/s/${s.slug}`} className="block">
        <div className="relative">
          <HoverPreviewThumb story={s} />
          {s.category !== "general" && (
            <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-full bg-black/70 backdrop-blur text-white z-10">
              {CATEGORY_LABEL[s.category] || s.category}
            </span>
          )}
          {s.verified && (
            <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-full bg-emerald-500/85 text-black font-medium z-10" title={t("badge_verified")}>
              <ShieldCheck className="w-3 h-3" /> {t("badge_verified")}
            </span>
          )}
        </div>
        <div className="p-5">
          <div className="font-display text-lg leading-snug group-hover:text-[color:var(--accent)] transition-colors">
            {s.title}
          </div>
          {s.location && (
            <div className="flex items-center gap-1 text-[11px] text-[color:var(--muted)] mt-2">
              <MapPin className="w-3 h-3" /> {s.location}
            </div>
          )}
          {s.impact_summary && (
            <p className="text-sm text-[color:var(--muted)] mt-3 leading-relaxed line-clamp-3">
              {s.impact_summary}
            </p>
          )}
        </div>
      </a>
      <div className="px-5 pb-5 flex items-center gap-2">
        <a href={`/s/${s.slug}`} className="btn-ghost text-xs flex-1 text-center inline-flex items-center justify-center gap-1.5"
           data-testid={`impact-read-${s.slug}`}>
          {t("card_read")} <ArrowRight className="w-3 h-3" />
        </a>
        {donate && (
          <a href={donate.url} target="_blank" rel="noopener noreferrer"
             className="btn-primary text-xs inline-flex items-center gap-1.5"
             style={{ background: "#10b981", color: "#04150d" }}
             data-testid={`impact-support-${s.slug}`}>
            <Heart className="w-3 h-3" /> {t("card_support")}
          </a>
        )}
        {status === "goal_reached" && (
          <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border"
                style={{ background: "rgba(16, 185, 129, 0.1)", borderColor: "rgba(16, 185, 129, 0.3)", color: "#34d399" }}
                data-testid={`impact-goal-reached-${s.slug}`}>
            <Heart className="w-3 h-3 fill-current" /> Goal reached · thank you
          </span>
        )}
      </div>
    </div>
  );
}
